import { Helmet } from 'react-helmet';
import { useState } from 'react';
import styles from './FeaturesGroups.module.css';
import mcpDiagramUrl from '../assets/mcp-server-diagram.svg?url';

export default function AIFeatures() {
  const [diagramLoadFailed, setDiagramLoadFailed] = useState(false);

  return (
    <>
      <Helmet>
        <title>AI Features | MCP Server Integration | MyPeopleDatabase</title>
        <meta
          name="description"
          content="Learn how MyPeopleDatabase integrates with AI tools via a local MCP (Model Context Protocol) server, enabling assistants to search, list, and summarize your contacts and groups."
        />
        <meta
          name="keywords"
          content="MyPeopleDatabase AI, MCP server, Model Context Protocol, Claude Desktop, Cursor, contact manager AI"
        />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.overlay} />
        <div className={styles.content}>
          <h1 className={styles.title}>AI Features: Assistants + MCP Server</h1>

          <div className={styles.introSection}>
            <p className={styles.subtitle}>
              MyPeopleDatabase includes a local MCP (Model Context Protocol) server so AI tools can help you
              search, understand, and use your people data without you manually digging through records.
            </p>
            <p className={styles.subtitle}>
              The MCP server is currently intended for agents that can connect locally to your desktop app (for
              example, via localhost). Web and cloud-based MCP integration is not available yet.
            </p>
          </div>

          <div className={styles.featuresContainer}>
            <div className={styles.feature}>
              <h2>Local MCP Server (localhost)</h2>
              <p>
                Start an MCP server from inside the desktop app. Your AI tool connects to your machine over
                localhost and can request data through a controlled set of tools.
              </p>
            </div>

            <div className={styles.feature}>
              <h2>Search and Retrieval Tools</h2>
              <p>
                The MCP server exposes specific tools like searching by name/email/notes, fetching a person by
                ID, and listing people with optional limits.
              </p>
            </div>

            <div className={styles.feature}>
              <h2>Groups + Birthdays</h2>
              <p>
                AI can list groups (with member counts) and pull birthdays (by month or upcoming), helping you
                plan outreach and keep relationships warm.
              </p>
            </div>

            <div className={styles.feature}>
              <h2>Works with MCP-Compatible Apps</h2>
              <p>
                Connect tools like Claude Desktop, Cursor, or other MCP-capable clients that can speak JSON-RPC
                over HTTP.
              </p>
            </div>

            <div className={styles.feature}>
              <h2>Privacy-First by Design</h2>
              <p>
                You decide when the server is running, what port it uses, and which AI tool you connect. The
                server only exposes a limited, documented tool set.
              </p>
            </div>

            <div className={styles.feature}>
              <h2>Fast Workflow for Real Tasks</h2>
              <p>
                Ask your assistant to identify the right contact, summarize what you know about them, or pull a
                short list for a group message—then you decide what to do next.
              </p>
            </div>
          </div>

          <div className={styles.useCasesSection}>
            <h2>MCP Server Diagram</h2>
            <div style={{ width: '100%', maxWidth: '1100px', margin: '0 auto' }}>
              {!diagramLoadFailed && (
                <img
                  src={mcpDiagramUrl}
                  alt="Diagram showing an AI tool connecting to the MyPeopleDatabase MCP server on localhost"
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '8px',
                    border: '1px solid rgba(84, 106, 255, 0.35)',
                    background: 'rgba(21, 28, 55, 0.35)',
                  }}
                  onError={() => setDiagramLoadFailed(true)}
                />
              )}
              {diagramLoadFailed && (
                <p style={{ color: '#e3e8ff', marginTop: '12px', textAlign: 'left', lineHeight: 1.6 }}>
                  Diagram failed to load. You can open it directly here:{' '}
                  <a href={mcpDiagramUrl} style={{ color: '#8494ff' }}>
                    MCP server diagram
                  </a>
                </p>
              )}
              <p style={{ color: '#e3e8ff', marginTop: '12px', textAlign: 'left', lineHeight: 1.6 }}>
                The desktop app runs an MCP server on your machine. Your AI tool calls MCP methods (tools/list,
                tools/call) over HTTP to query your contacts and groups.
              </p>
            </div>
          </div>

          <div className={styles.useCasesSection}>
            <h2>What You Can Ask Your AI</h2>
            <div className={styles.useCasesGrid}>
              <div className={styles.useCase}>
                <h3>Find People Fast</h3>
                <ul>
                  <li>
                    <strong>Search:</strong> “Find people named John who I met at a conference.”
                  </li>
                  <li>
                    <strong>Disambiguate:</strong> “Which Sarah works at Acme?”
                  </li>
                  <li>
                    <strong>Recall context:</strong> “Show me people with notes mentioning ‘ski trip’.”
                  </li>
                </ul>
              </div>

              <div className={styles.useCase}>
                <h3>Plan Outreach</h3>
                <ul>
                  <li>
                    <strong>Birthdays:</strong> “Who has a birthday this month?”
                  </li>
                  <li>
                    <strong>Upcoming:</strong> “List upcoming birthdays in the next 30 days.”
                  </li>
                  <li>
                    <strong>Follow-ups:</strong> “Give me a short list of people I should reconnect with.”
                  </li>
                </ul>
              </div>

              <div className={styles.useCase}>
                <h3>Work with Groups</h3>
                <ul>
                  <li>
                    <strong>List groups:</strong> “What groups do I have and how big are they?”
                  </li>
                  <li>
                    <strong>Targeting:</strong> “Which group should I use for alumni outreach?”
                  </li>
                  <li>
                    <strong>Prep:</strong> “Draft a message template for this group (I’ll send it myself).”
                  </li>
                </ul>
              </div>

              <div className={styles.useCase}>
                <h3>Get a Clean Summary</h3>
                <ul>
                  <li>
                    <strong>Single contact:</strong> “Summarize what I know about this person.”
                  </li>
                  <li>
                    <strong>Shortlists:</strong> “Give me the top 10 relevant contacts for this project.”
                  </li>
                  <li>
                    <strong>Sanity check:</strong> “Do I have duplicate entries for this person?”
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className={styles.benefitsSection}>
            <h2>Why It Matters</h2>
            <div className={styles.benefitsGrid}>
              <div className={styles.benefit}>
                <h3>Speed</h3>
                <p>Ask a question in plain English instead of clicking through screens.</p>
              </div>

              <div className={styles.benefit}>
                <h3>Better Recall</h3>
                <p>Turn your notes, groups, and contact metadata into something you can query naturally.</p>
              </div>

              <div className={styles.benefit}>
                <h3>Local Control</h3>
                <p>The server runs on your machine and is off unless you turn it on.</p>
              </div>

              <div className={styles.benefit}>
                <h3>Tool-Limited Access</h3>
                <p>The MCP server exposes a defined set of tools for safe, predictable queries.</p>
              </div>

              <div className={styles.benefit}>
                <h3>Practical Use Cases</h3>
                <p>Birthdays, outreach, finding the right person, and summarizing context are where AI shines.</p>
              </div>

              <div className={styles.benefit}>
                <h3>Works with Your Workflow</h3>
                <p>Use your preferred AI tool, then decide what actions to take in MyPeopleDatabase.</p>
              </div>
            </div>
          </div>

          <div className={styles.ctaSection}>
            <h2>Try AI Features in MyPeopleDatabase</h2>
            <p>
              Download the desktop app, open the AI tab, start the MCP server, and connect your AI assistant.
            </p>
            <div className={styles.ctaButtons}>
              <a href="/download" className={styles.primaryButton}>
                Download Now
              </a>
              <a href="/features" className={styles.secondaryButton}>
                View All Features
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
